/**
 * The app shell: layout switching and the grid of independent players.
 *
 * Cell state (what is queued where) is persisted in the encrypted `settings`
 * item, so reopening the tab restores the wall exactly — and the server still
 * only sees a blob.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  LAYOUTS,
  MAX_CELLS,
  cellCount,
  type CellState,
  type Item,
  type LayoutId,
  type VideoPayload,
} from './types';
import { Library } from './ui/Library';
import { Splitter } from './ui/Splitter';
import { PlayerCell } from './ui/PlayerCell';
import { Unlock } from './ui/Unlock';
import { useVault } from './vault';

function gb(bytes: number): string {
  if (bytes < 1024 ** 2) return `${Math.round(bytes / 1024)}KB`;
  if (bytes < 1024 ** 3) return `${Math.round(bytes / 1024 ** 2)}MB`;
  return `${(bytes / 1024 ** 3).toFixed(1)}GB`;
}

/** Phone-width, matching the media query the sidebar's styling uses. */
function isNarrow(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(max-width: 720px)').matches;
}

/** Pad to the full complement without ever dropping an assignment. */
function padCells(cells: (CellState | null)[]): (CellState | null)[] {
  const next = cells.slice(0, MAX_CELLS);
  while (next.length < MAX_CELLS) next.push(null);
  return next;
}

export function App() {
  const { status, videos, settings, saveSettings, lock, signOut, username, error, storage, clearAllCached } =
    useVault();
  /**
   * The slot the library sends things to.
   *
   * Deliberately not persisted: it is where attention is right now, not a
   * property of the wall. Saving it would also mean a server write on every
   * click of a cell.
   */
  const [activeCell, setActiveCell] = useState<number | null>(0);
  /**
   * Sidebar visibility.
   *
   * Open by default on a wide screen, where it sits beside the wall; closed on
   * a phone, where it slides over the top of it. Not persisted — which side of
   * the screen you are looking at is a moment's intent, not a property of the
   * wall.
   */
  const [libraryOpen, setLibraryOpen] = useState(() => !isNarrow());
  const gridRef = useRef<HTMLDivElement>(null);
  /** Live split during a drag; settings catch up when the pointer is released. */
  const [dragSplit, setDragSplit] = useState<{ x: number; y: number } | null>(null);

  const byUuid = useMemo(
    () => new Map(videos.map((v) => [v.uuid, v] as const)),
    [videos],
  );

  // Every slot, always. `visible` is the window the current layout shows.
  const cells = useMemo(() => padCells(settings.cells), [settings.cells]);
  const visible = useMemo(
    () => cells.slice(0, cellCount(settings.layout)),
    [cells, settings.layout],
  );

  const update = useCallback(
    (next: Partial<typeof settings>) => void saveSettings({ ...settings, ...next }),
    [saveSettings, settings],
  );

  // Only the layout changes. Assignments and the audio choice are left intact
  // so a round trip through another layout restores exactly what was playing.
  const setLayout = useCallback((layout: LayoutId) => update({ layout }), [update]);

  const split = dragSplit ?? settings.split ?? { x: 0.5, y: 0.5 };
  const hasColumns = settings.layout === 'cols2' || settings.layout === 'grid4';
  const hasRows = settings.layout === 'rows2' || settings.layout === 'grid4';
  const gridStyle = {
    gridTemplateColumns: hasColumns ? `${split.x}fr ${1 - split.x}fr` : '1fr',
    gridTemplateRows: hasRows ? `${split.y}fr ${1 - split.y}fr` : '1fr',
  };

  const setCell = useCallback(
    (index: number, cell: CellState | null) => {
      const next = cells.slice();
      next[index] = cell;
      update({ cells: next });
    },
    [cells, update],
  );

  // A shrinking layout can strand the selection past the end of the grid.
  const target = activeCell === null ? null : Math.min(activeCell, visible.length - 1);

  /** The next slot with nothing in it, or null when the wall is full. */
  const nextEmpty = useCallback(
    (after: number) => {
      for (let step = 1; step <= visible.length; step += 1) {
        const i = (after + step) % visible.length;
        const cell = visible[i];
        if (!cell || cell.queue.length === 0) return i;
      }
      return null;
    },
    [visible],
  );

  const playOne = useCallback(
    (video: Item<VideoPayload>) => {
      // A single video keeps the slot selected: trying several in the same
      // pane is the common case, and advancing would make that two clicks.
      if (target === null) return;
      setCell(target, { playlist: null, queue: [video.uuid], index: 0 });
    },
    [setCell, target],
  );

  const playQueue = useCallback(
    (queue: string[]) => {
      if (target === null || queue.length === 0) return;
      setCell(target, { playlist: null, queue, index: 0 });
      // Filling a wall is the point of assigning a whole folder, so move on to
      // the next empty pane — and stop selecting anything once none are left,
      // rather than silently overwriting what was just placed.
      setActiveCell(nextEmpty(target));
    },
    [nextEmpty, setCell, target],
  );

  /** Step through a slot's queue. Wraps, so a wall never goes dark. */
  const step = useCallback(
    (index: number, delta: number) => {
      const cell = cells[index];
      if (!cell || cell.queue.length === 0) return;
      const n = cell.queue.length;
      setCell(index, { ...cell, index: (cell.index + delta + n) % n });
    },
    [cells, setCell],
  );

  const chromeHidden = settings.chromeHidden ?? false;
  const toggleChrome = useCallback(
    () => update({ chromeHidden: !chromeHidden }),
    [chromeHidden, update],
  );

  // `h` hides the bar, `l` the library — except while typing, where a field
  // that eats its own letters is worse than no shortcut at all.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.key !== 'h' && event.key !== 'l') return;
      const target = event.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target?.isContentEditable) {
        return;
      }
      event.preventDefault();
      if (event.key === 'h') toggleChrome();
      else setLibraryOpen((open) => !open);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toggleChrome]);

  if (status === 'checking') return <div className="boot">…</div>;
  if (status !== 'unlocked') return <Unlock />;

  return (
    <div className="app">
      {chromeHidden && (
        // Small, dim handles rather than nothing at all: chrome you cannot
        // find your way back to is a trap. The library needs its own, because
        // its button lives in the bar that is hidden — otherwise the keyboard
        // would be the only way back to it.
        <div className="chrome-peek">
          <button
            type="button"
            onClick={() => setLibraryOpen((open) => !open)}
            title={libraryOpen ? 'Hide the library' : 'Show the library'}
            aria-label={libraryOpen ? 'Hide the library' : 'Show the library'}
          >
            ☰
          </button>
          <button
            type="button"
            onClick={toggleChrome}
            title="Show the controls"
            aria-label="Show the controls"
          >
            ▾
          </button>
        </div>
      )}

      {!chromeHidden && (
      <header className="topbar">
        <button
          type="button"
          className="chip"
          onClick={() => setLibraryOpen((open) => !open)}
          aria-expanded={libraryOpen}
          title={libraryOpen ? 'Hide the library (l)' : 'Show the library (l)'}
        >
          library
        </button>
        <span className="topbar__logo">peepshow</span>
        <div className="topbar__layouts">
          {LAYOUTS.map((l) => (
            <button
              key={l.id}
              type="button"
              className={l.id === settings.layout ? 'chip chip--on' : 'chip'}
              onClick={() => setLayout(l.id)}
              title={`${l.cells} player${l.cells === 1 ? '' : 's'}`}
            >
              {l.label}
            </button>
          ))}
        </div>
        <span className="topbar__spacer" />

        {storage && (
          <button
            type="button"
            className="chip"
            onClick={() => {
              if (confirm('Delete every cached video from this device?')) void clearAllCached();
            }}
            title={
              `${gb(storage.usage)} of ${gb(storage.quota)} used on this device ` +
              `(${Math.round((storage.usage / storage.quota) * 100)}%). ` +
              'Click to clear the cache. Nothing on the server is touched.'
            }
          >
            {gb(storage.usage)} / {gb(storage.quota)}
          </button>
        )}

        <span className="topbar__user">{username}</span>
        <button type="button" className="chip" onClick={lock} title="Lock the vault">
          lock
        </button>
        <button type="button" className="chip" onClick={() => void signOut()}>
          sign out
        </button>
        <button
          type="button"
          className="chip"
          onClick={toggleChrome}
          title="Hide the controls (h)"
          aria-label="Hide the controls"
        >
          ▴
        </button>
      </header>
      )}

      {error && (
        <div className="banner" role="alert">
          Your changes are not being saved: {error}
        </div>
      )}

      <main className="main">
        {/* Always mounted, so it can fade out as well as in. Disabled while
            closed rather than unmounted: an invisible button must not be
            reachable by tab or by a stray tap. */}
        <button
          type="button"
          className={libraryOpen ? 'library__scrim library__scrim--open' : 'library__scrim'}
          aria-label="Close the library"
          aria-hidden={!libraryOpen}
          disabled={!libraryOpen}
          onClick={() => setLibraryOpen(false)}
        />
        <div className={libraryOpen ? 'library-shell library-shell--open' : 'library-shell'}>
          <Library
            slots={visible.map((cell, index) => ({
              index,
              title: cell?.queue[cell.index]
                ? (byUuid.get(cell.queue[cell.index])?.payload.title ?? 'missing')
                : null,
            }))}
            target={target}
            onTarget={setActiveCell}
            layout={settings.layout}
            split={split}
            // Only a phone closes on a pick: there the library covers the wall,
            // so staying open would hide the thing just chosen. Beside the wall
            // it should stay put for the next pick.
            onPlay={(video) => {
              playOne(video);
              if (isNarrow()) setLibraryOpen(false);
            }}
            onPlayQueue={(queue) => {
              playQueue(queue);
              if (isNarrow()) setLibraryOpen(false);
            }}
          />
        </div>
        <div className={`grid grid--${settings.layout}`} style={gridStyle} ref={gridRef}>
          {visible.map((cell, i) => {
            const uuid = cell?.queue[cell.index] ?? null;
            const video = uuid ? (byUuid.get(uuid) ?? null) : null;
            return (
              <PlayerCell
                // Keyed by slot, not by video: a queue advance should swap
                // the source, not tear the whole cell down and build it again.
                key={i}
                video={video}
                selected={i === target && visible.length > 1}
                // Highlighted only on a wall of several; aimed at regardless,
                // which is what the keyboard follows.
                active={i === target}
                slideSpeed={uuid ? settings.slideSpeeds?.[uuid] : undefined}
                onSlideSpeed={(seconds) =>
                  uuid && update({ slideSpeeds: { ...(settings.slideSpeeds ?? {}), [uuid]: seconds } })
                }
                onSelect={() => setActiveCell(i)}
                label={
                  i === target
                    ? 'Selected — pick something from the library'
                    : `Slot ${i + 1} — click to select`
                }
                volume={uuid ? (settings.volumes?.[uuid] ?? 0) : 0}
                onVolume={(v) =>
                  uuid && update({ volumes: { ...(settings.volumes ?? {}), [uuid]: v } })
                }
                queueLength={cell?.queue.length ?? 0}
                queuePosition={cell?.index ?? 0}
                onNext={() => step(i, 1)}
                onPrev={() => step(i, -1)}
                onEnded={() => step(i, 1)}
                onClear={() => setCell(i, null)}
              />
            );
          })}
          {(hasColumns || hasRows) && (
            <Splitter
              vertical={hasColumns}
              horizontal={hasRows}
              split={split}
              gridRef={gridRef}
              onPreview={setDragSplit}
              onCommit={(next) => {
                setDragSplit(null);
                update({ split: next });
              }}
            />
          )}
        </div>
      </main>
    </div>
  );
}
